/**
 * The runtime airport database: a `DataView` over the baked binary, plus queries.
 *
 * Nothing is deserialised on load. The whole file — about 1.2 MB for 11,776 airports and
 * 15,253 runways — is fetched once, kept as an `ArrayBuffer`, and read through a `DataView`
 * when asked. An {@link Airport} object is materialised only for the handful anyone actually
 * looks at, and even then it is a snapshot rather than a live view.
 *
 * That shape is what makes the queries cheap enough to run without a spatial index:
 * {@link AirportDatabase.nearest} is a linear scan of 11,776 records, each a pair of `int32`
 * reads and a squared distance, with no allocation and no pointer chasing — well under a
 * millisecond, and it runs when the pilot opens the spawn menu rather than every frame. A
 * quadtree here would be more code, more memory, and slower to build than the scan it replaces.
 *
 * `updateFixed` / `updateFrame`: neither. Loaded once, queried on demand.
 */

import {
  AIRPORT,
  AIRPORT_DB_MAGIC,
  AIRPORT_DB_VERSION,
  AIRPORT_RECORD_BYTES,
  COORD_SCALE,
  HEADER_BYTES,
  HEADING_SCALE,
  PLACEMENT,
  RUNWAY,
  RUNWAY_LIGHTED,
  RUNWAY_RECORD_BYTES,
  SURFACES,
  type PlacementQuality,
  type RunwaySurface,
} from './format.js';

/** One runway, as the renderer and the spawn logic want it. */
export interface Runway {
  /** Designator of the low-numbered end, e.g. "10L". */
  lowIdent: string;
  /** Designator of the reciprocal end, e.g. "28R". */
  highIdent: string;
  lowLatitudeDeg: number;
  lowLongitudeDeg: number;
  highLatitudeDeg: number;
  highLongitudeDeg: number;
  /** Threshold elevations [m MSL]; the pair gives the runway's slope. */
  lowElevationM: number;
  highElevationM: number;
  lengthM: number;
  widthM: number;
  /** True heading of the low end [deg]. */
  headingDegTrue: number;
  surface: RunwaySurface;
  lighted: boolean;
  /** How trustworthy the geometry is — see `format.ts`. */
  placement: PlacementQuality;
}

export interface Airport {
  icao: string;
  name: string;
  latitudeDeg: number;
  longitudeDeg: number;
  elevationM: number;
  runways: Runway[];
}

/** A search or proximity hit, without materialising the whole airport. */
export interface AirportSummary {
  index: number;
  icao: string;
  name: string;
  latitudeDeg: number;
  longitudeDeg: number;
  elevationM: number;
  runwayCount: number;
  /** Great-circle distance from the query point [m]; 0 for a text search. */
  distanceM: number;
  /** Length of the airport's longest runway [m]. */
  longestRunwayM: number;
}

const EARTH_MEAN_RADIUS_M = 6_371_008.8;
const DEG2RAD = Math.PI / 180;

export class AirportDatabase {
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  private readonly decoder = new TextDecoder();

  readonly airportCount: number;
  readonly runwayCount: number;

  private readonly textBase: number;
  private readonly airportBase: number;
  private readonly runwayBase: number;

  /** ICAO → record index, built lazily on the first lookup. */
  private icaoIndex: Map<string, number> | null = null;

  constructor(buffer: ArrayBuffer) {
    this.view = new DataView(buffer);
    this.bytes = new Uint8Array(buffer);

    if (buffer.byteLength < HEADER_BYTES) {
      throw new Error('airport database: file is too short to contain a header');
    }
    const magic = this.view.getUint32(0, true);
    if (magic !== AIRPORT_DB_MAGIC) {
      throw new Error(
        `airport database: bad magic 0x${magic.toString(16)} — re-run \`npm run bake:airports\``,
      );
    }
    const version = this.view.getUint32(4, true);
    if (version !== AIRPORT_DB_VERSION) {
      throw new Error(
        `airport database: version ${version}, expected ${AIRPORT_DB_VERSION} — ` +
          're-run `npm run bake:airports`',
      );
    }

    this.airportCount = this.view.getUint32(8, true);
    this.runwayCount = this.view.getUint32(12, true);
    const textBytes = this.view.getUint32(16, true);

    this.textBase = HEADER_BYTES;
    this.airportBase = this.textBase + textBytes;
    this.runwayBase = this.airportBase + this.airportCount * AIRPORT_RECORD_BYTES;

    const expected = this.runwayBase + this.runwayCount * RUNWAY_RECORD_BYTES;
    if (buffer.byteLength < expected) {
      throw new Error(
        `airport database: truncated — expected ${expected} bytes, got ${buffer.byteLength}`,
      );
    }
  }

  /** Fetch and open the baked database. */
  static async load(url: string, signal?: AbortSignal): Promise<AirportDatabase> {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`airport database: ${url} → HTTP ${response.status}`);
    }
    return new AirportDatabase(await response.arrayBuffer());
  }

  private text(offset: number, length: number): string {
    if (length === 0) return '';
    return this.decoder.decode(this.bytes.subarray(this.textBase + offset, this.textBase + offset + length));
  }

  private airportAt(index: number): number {
    return this.airportBase + index * AIRPORT_RECORD_BYTES;
  }

  /** ICAO code of a record, without building the rest of it. */
  icaoAt(index: number): string {
    const at = this.airportAt(index);
    return this.text(
      this.view.getUint32(at + AIRPORT.icaoOffset, true),
      this.view.getUint8(at + AIRPORT.icaoLength),
    );
  }

  latitudeAt(index: number): number {
    return this.view.getInt32(this.airportAt(index) + AIRPORT.latitudeE7, true) / COORD_SCALE;
  }

  longitudeAt(index: number): number {
    return this.view.getInt32(this.airportAt(index) + AIRPORT.longitudeE7, true) / COORD_SCALE;
  }

  /** Build the full record for one airport, runways included. */
  airport(index: number): Airport {
    const at = this.airportAt(index);
    const runwayIndex = this.view.getUint32(at + AIRPORT.runwayIndex, true);
    const runwayCount = this.view.getUint16(at + AIRPORT.runwayCount, true);

    const runways: Runway[] = [];
    for (let r = 0; r < runwayCount; r++) runways.push(this.runway(runwayIndex + r));

    return {
      icao: this.icaoAt(index),
      name: this.text(
        this.view.getUint32(at + AIRPORT.nameOffset, true),
        this.view.getUint16(at + AIRPORT.nameLength, true),
      ),
      latitudeDeg: this.view.getInt32(at + AIRPORT.latitudeE7, true) / COORD_SCALE,
      longitudeDeg: this.view.getInt32(at + AIRPORT.longitudeE7, true) / COORD_SCALE,
      elevationM: this.view.getInt16(at + AIRPORT.elevationM, true),
      runways,
    };
  }

  runway(index: number): Runway {
    const at = this.runwayBase + index * RUNWAY_RECORD_BYTES;
    const identOffset = this.view.getUint32(at + RUNWAY.identOffset, true);
    const lowLength = this.view.getUint8(at + RUNWAY.lowIdentLength);
    const highLength = this.view.getUint8(at + RUNWAY.highIdentLength);

    return {
      lowIdent: this.text(identOffset, lowLength),
      // The high ident is stored immediately after the low one.
      highIdent: this.text(identOffset + lowLength, highLength),
      lowLatitudeDeg: this.view.getInt32(at + RUNWAY.lowLatitudeE7, true) / COORD_SCALE,
      lowLongitudeDeg: this.view.getInt32(at + RUNWAY.lowLongitudeE7, true) / COORD_SCALE,
      highLatitudeDeg: this.view.getInt32(at + RUNWAY.highLatitudeE7, true) / COORD_SCALE,
      highLongitudeDeg: this.view.getInt32(at + RUNWAY.highLongitudeE7, true) / COORD_SCALE,
      lowElevationM: this.view.getInt16(at + RUNWAY.lowElevationM, true),
      highElevationM: this.view.getInt16(at + RUNWAY.highElevationM, true),
      lengthM: this.view.getUint16(at + RUNWAY.lengthM, true),
      widthM: this.view.getUint16(at + RUNWAY.widthM, true),
      headingDegTrue: this.view.getUint16(at + RUNWAY.headingDeciDeg, true) / HEADING_SCALE,
      surface: SURFACES[this.view.getUint8(at + RUNWAY.surface)] ?? 'unknown',
      lighted: (this.view.getUint8(at + RUNWAY.flags) & RUNWAY_LIGHTED) !== 0,
      placement: this.view.getUint8(at + RUNWAY.placement) as PlacementQuality,
    };
  }

  /** Longest runway at an airport [m], read without building any objects. */
  longestRunwayM(index: number): number {
    const at = this.airportAt(index);
    const first = this.view.getUint32(at + AIRPORT.runwayIndex, true);
    const count = this.view.getUint16(at + AIRPORT.runwayCount, true);
    let longest = 0;
    for (let r = 0; r < count; r++) {
      const length = this.view.getUint16(
        this.runwayBase + (first + r) * RUNWAY_RECORD_BYTES + RUNWAY.lengthM,
        true,
      );
      if (length > longest) longest = length;
    }
    return longest;
  }

  /** Look an airport up by ICAO. Case-insensitive. */
  byIcao(icao: string): Airport | null {
    const index = this.indexOfIcao(icao);
    return index < 0 ? null : this.airport(index);
  }

  indexOfIcao(icao: string): number {
    if (this.icaoIndex === null) {
      this.icaoIndex = new Map();
      for (let i = 0; i < this.airportCount; i++) this.icaoIndex.set(this.icaoAt(i), i);
    }
    return this.icaoIndex.get(icao.trim().toUpperCase()) ?? -1;
  }

  /**
   * The nearest airports to a point, closest first.
   *
   * A linear scan with a squared-distance comparison in an equirectangular approximation: no
   * trigonometry per record, no allocation until the results are built, and the exact
   * great-circle distance computed only for the handful that survive. At 11,776 records this
   * runs in well under a millisecond, which is a fraction of the cost of the spatial index it
   * would take to beat it.
   */
  nearest(
    latitudeDeg: number,
    longitudeDeg: number,
    limit = 10,
    maxDistanceM = Number.POSITIVE_INFINITY,
  ): AirportSummary[] {
    const cosLat = Math.cos(latitudeDeg * DEG2RAD);
    const metresPerDegree = DEG2RAD * EARTH_MEAN_RADIUS_M;
    const maxDegrees = maxDistanceM / metresPerDegree;
    const maxDegreesSq = maxDegrees * maxDegrees;

    // A small insertion-sorted top-N rather than sorting 11,776 candidates.
    const bestIndices: number[] = [];
    const bestScores: number[] = [];

    for (let i = 0; i < this.airportCount; i++) {
      const at = this.airportAt(i);
      const lat = this.view.getInt32(at + AIRPORT.latitudeE7, true) / COORD_SCALE;
      const dLat = lat - latitudeDeg;
      if (dLat * dLat > maxDegreesSq) continue;

      const lon = this.view.getInt32(at + AIRPORT.longitudeE7, true) / COORD_SCALE;
      let dLon = lon - longitudeDeg;
      if (dLon > 180) dLon -= 360;
      else if (dLon < -180) dLon += 360;
      const dLonScaled = dLon * cosLat;

      const score = dLat * dLat + dLonScaled * dLonScaled;
      if (score > maxDegreesSq) continue;
      if (bestScores.length === limit && score >= bestScores[bestScores.length - 1]) continue;

      let position = bestScores.length;
      while (position > 0 && bestScores[position - 1] > score) position--;
      bestScores.splice(position, 0, score);
      bestIndices.splice(position, 0, i);
      if (bestScores.length > limit) {
        bestScores.pop();
        bestIndices.pop();
      }
    }

    return bestIndices.map((index) =>
      this.summary(index, greatCircleDistanceM(latitudeDeg, longitudeDeg, this.latitudeAt(index), this.longitudeAt(index))),
    );
  }

  /**
   * Search by ICAO prefix or by words in the name.
   *
   * Ranked so an exact ICAO always wins, then ICAO prefixes, then name matches, and within
   * each band by runway length — because when someone types "LON" they want Heathrow before
   * they want a 600 m strip in Ontario.
   */
  search(query: string, limit = 12): AirportSummary[] {
    const trimmed = query.trim().toUpperCase();
    if (trimmed === '') return [];

    const scored: Array<{ index: number; rank: number; longest: number }> = [];

    for (let i = 0; i < this.airportCount; i++) {
      const icao = this.icaoAt(i);
      let rank = -1;

      if (icao === trimmed) rank = 0;
      else if (icao.startsWith(trimmed)) rank = 1;
      else {
        const at = this.airportAt(i);
        const name = this.text(
          this.view.getUint32(at + AIRPORT.nameOffset, true),
          this.view.getUint16(at + AIRPORT.nameLength, true),
        ).toUpperCase();
        if (name.startsWith(trimmed)) rank = 2;
        else if (name.includes(trimmed)) rank = 3;
      }

      if (rank >= 0) scored.push({ index: i, rank, longest: this.longestRunwayM(i) });
    }

    scored.sort((a, b) => a.rank - b.rank || b.longest - a.longest);
    return scored.slice(0, limit).map((entry) => this.summary(entry.index, 0));
  }

  private summary(index: number, distanceM: number): AirportSummary {
    const at = this.airportAt(index);
    return {
      index,
      icao: this.icaoAt(index),
      name: this.text(
        this.view.getUint32(at + AIRPORT.nameOffset, true),
        this.view.getUint16(at + AIRPORT.nameLength, true),
      ),
      latitudeDeg: this.view.getInt32(at + AIRPORT.latitudeE7, true) / COORD_SCALE,
      longitudeDeg: this.view.getInt32(at + AIRPORT.longitudeE7, true) / COORD_SCALE,
      elevationM: this.view.getInt16(at + AIRPORT.elevationM, true),
      runwayCount: this.view.getUint16(at + AIRPORT.runwayCount, true),
      distanceM,
      longestRunwayM: this.longestRunwayM(index),
    };
  }
}

/** The runway with the most pavement — the sensible default to spawn on. */
export function longestRunway(airport: Airport): Runway | null {
  let best: Runway | null = null;
  for (const runway of airport.runways) {
    if (best === null || runway.lengthM > best.lengthM) best = runway;
  }
  return best;
}

/** Is this runway good enough to fly an approach to, or only good enough to look at? */
export function isSurveyed(runway: Runway): boolean {
  return runway.placement === PLACEMENT.surveyed;
}

function greatCircleDistanceM(
  latA: number,
  lonA: number,
  latB: number,
  lonB: number,
): number {
  const dLat = (latB - latA) * DEG2RAD;
  const dLon = (lonB - lonA) * DEG2RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(latA * DEG2RAD) * Math.cos(latB * DEG2RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_MEAN_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}
